import React, { useEffect, useState } from 'react';
import { TourCard } from './TourCard';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, FilterIcon } from 'lucide-react';
interface TourGridProps {
  activeFilter: string;
  searchQuery: string;
  sortOption: string;
  viewMode: string;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  onTourClick: (tourId: string) => void;
}
export function TourGrid({
  activeFilter,
  searchQuery,
  sortOption,
  viewMode,
  currentPage,
  setCurrentPage,
  onTourClick
}: TourGridProps) {
  const [loading, setLoading] = useState(true);
  const [tours, setTours] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(15);
  // Mock tours data
  const mockTours = [{
    id: 'tour1',
    title: 'Swiss Alpine Adventure',
    image: 'https://images.unsplash.com/photo-1527668752968-14dc70a27c95?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Adventure',
    location: 'Zurich, Interlaken, Zermatt',
    rating: 4.8,
    reviewCount: 234,
    highlights: ['Matterhorn cable car experience', 'Traditional Swiss village visits', 'Professional photography guide', 'Local cuisine tasting'],
    originalPrice: 2500,
    currentPrice: 1899,
    discount: 24,
    isBestSeller: true,
    limitedSeats: true,
    duration: '7 Days',
    availableSeats: 3
  }, {
    id: 'tour2',
    title: 'Luxury Switzerland Experience',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Luxury',
    location: 'Geneva, Lausanne, Montreux',
    rating: 4.9,
    reviewCount: 127,
    highlights: ['5-star accommodations throughout', 'Private helicopter tour', 'Exclusive wine tasting experiences', 'Michelin-starred dining'],
    originalPrice: 4200,
    currentPrice: 3299,
    discount: 21,
    isBestSeller: false,
    limitedSeats: false,
    duration: '5 Days',
    availableSeats: 8
  }, {
    id: 'tour3',
    title: 'Swiss Cities & Culture',
    image: 'https://images.unsplash.com/photo-1515488764276-beab7607c1e6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Cultural',
    location: 'Zurich, Bern, Basel',
    rating: 4.7,
    reviewCount: 98,
    highlights: ['Museum and gallery tours', 'Historical walking tours', 'Swiss watchmaking demonstration', 'Cultural performances'],
    originalPrice: 1500,
    currentPrice: 1299,
    discount: 13,
    isBestSeller: false,
    limitedSeats: false,
    duration: '4 Days',
    availableSeats: 12
  }, {
    id: 'tour4',
    title: 'Adventure Swiss Alps',
    image: 'https://images.unsplash.com/photo-1508424897381-4fd8755e4b7a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Adventure',
    location: 'Interlaken, Grindelwald, Lauterbrunnen',
    rating: 4.8,
    reviewCount: 156,
    highlights: ['Paragliding over Alpine valleys', 'White water rafting', 'Guided hiking excursions', 'Overnight mountain hut stay'],
    originalPrice: 1999,
    currentPrice: 1799,
    discount: 10,
    isBestSeller: true,
    limitedSeats: false,
    duration: '6 Days',
    availableSeats: 5
  }, {
    id: 'tour5',
    title: 'Swiss Food & Wine Tour',
    image: 'https://images.unsplash.com/photo-1528190336454-13cd56b45b5a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Food & Wine',
    location: 'Geneva, Montreux, Lavaux',
    rating: 4.9,
    reviewCount: 87,
    highlights: ['Chocolate making workshop', 'Cheese fondue experience', 'Vineyard tours in Lavaux', 'Culinary walking tours'],
    originalPrice: 1200,
    currentPrice: 999,
    discount: 17,
    isBestSeller: false,
    limitedSeats: true,
    duration: '3 Days',
    availableSeats: 2
  }, {
    id: 'tour6',
    title: 'Photography Swiss Landscapes',
    image: 'https://images.unsplash.com/photo-1531210483974-4f8c1f33fd35?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Photography',
    location: 'Lucerne, Appenzell, St. Moritz',
    rating: 4.7,
    reviewCount: 64,
    highlights: ['Professional photography guidance', 'Sunrise and sunset shoots', 'Post-processing workshops', 'Hidden gem locations'],
    originalPrice: 2400,
    currentPrice: 2199,
    discount: 8,
    isBestSeller: false,
    limitedSeats: false,
    duration: '8 Days',
    availableSeats: 10
  }];
  // Simulate loading data
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setTours(mockTours);
      setLoading(false);
    }, 800);
  }, [activeFilter, searchQuery, sortOption, currentPage]);
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };
  // Active filters display (mock)
  const activeFilters = [{
    id: 'duration',
    label: 'Duration: 5-7 days'
  }, {
    id: 'price',
    label: 'Price: CHF 1000-3000'
  }, {
    id: 'type',
    label: 'Type: Adventure'
  }];
  return <section className="py-8" data-id="element-1788">
      <div className="container mx-auto px-4" data-id="element-1789">
        {/* Results header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6" data-id="element-1790">
          <h2 className="text-xl font-bold text-gray-900 mb-2 sm:mb-0" data-id="element-1791">
            127 tours found in Switzerland
          </h2>
          <div className="flex flex-wrap items-center gap-2" data-id="element-1792">
            {activeFilters.map(filter => <div key={filter.id} className="flex items-center bg-blue-50 text-blue-800 text-sm px-3 py-1 rounded-full" data-id="element-1793">
                <span data-id="element-1794">{filter.label}</span>
                <button className="ml-1 text-blue-600 hover:text-blue-800" data-id="element-1795">
                  <XIcon size={14} data-id="element-1796" />
                </button>
              </div>)}
            {activeFilters.length > 0 && <button className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-1797">
                Clear all
              </button>}
          </div>
        </div>

        {/* Loading skeleton */}
        {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1798">
            {[1, 2, 3, 4, 5, 6].map(skeleton => <div key={skeleton} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-1799">
                <div className="aspect-[3/2] bg-gray-200 animate-pulse" data-id="element-1800"></div>
                <div className="p-4" data-id="element-1801">
                  <div className="w-1/4 h-6 bg-gray-200 rounded animate-pulse mb-2" data-id="element-1802"></div>
                  <div className="w-3/4 h-6 bg-gray-200 rounded animate-pulse mb-2" data-id="element-1803"></div>
                  <div className="w-2/4 h-4 bg-gray-200 rounded animate-pulse mb-3" data-id="element-1804"></div>
                  <div className="w-full h-4 bg-gray-200 rounded animate-pulse mb-2" data-id="element-1805"></div>
                  <div className="w-full h-4 bg-gray-200 rounded animate-pulse mb-2" data-id="element-1806"></div>
                  <div className="w-3/4 h-4 bg-gray-200 rounded animate-pulse mb-4" data-id="element-1807"></div>
                  <div className="w-1/2 h-6 bg-gray-200 rounded animate-pulse mb-3" data-id="element-1808"></div>
                  <div className="w-full h-10 bg-gray-200 rounded animate-pulse" data-id="element-1809"></div>
                </div>
              </div>)}
          </div> : <>
            {/* Tour grid */}
            <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}`} data-id="element-1810">
              {tours.length > 0 ? tours.map(tour => <TourCard key={tour.id} tour={tour} data-id="element-1811" />) : <div className="col-span-3 py-16 text-center" data-id="element-1812">
                  <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center rounded-full bg-gray-100" data-id="element-1813">
                    <FilterIcon size={32} className="text-gray-400" data-id="element-1814" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1815">
                    No tours match your criteria
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-1816">
                    Try adjusting your filters or search terms
                  </p>
                  <button className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors" data-id="element-1817">
                    View all Switzerland tours
                  </button>
                </div>}
            </div>

            {/* Pagination */}
            {tours.length > 0 && <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1818">
                <div className="text-sm text-gray-600 mb-4 sm:mb-0" data-id="element-1819">
                  Showing <span className="font-medium" data-id="element-1820">1-6</span> of{' '}
                  <span className="font-medium" data-id="element-1821">127</span> tours
                </div>
                <div className="flex justify-center" data-id="element-1822">
                  <nav className="flex items-center" data-id="element-1823">
                    <button onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)} disabled={currentPage === 1} className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1824">
                      <ChevronLeftIcon size={20} data-id="element-1825" />
                    </button>
                    <div className="flex mx-1" data-id="element-1826">
                      {getPageNumbers().map((page, index) => page === '...' ? <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-600" data-id="element-1827">
                            ...
                          </span> : <button key={`page-${page}`} onClick={() => typeof page === 'number' && setCurrentPage(page)} className={`px-3 py-1 mx-1 rounded-md ${currentPage === page ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1828">
                            {page}
                          </button>)}
                    </div>
                    <button onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1829">
                      <ChevronRightIcon size={20} data-id="element-1830" />
                    </button>
                  </nav>
                </div>
                <button onClick={() => window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })} className="hidden sm:flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-1831">
                  Back to top
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1832">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" data-id="element-1833" />
                  </svg>
                </button>
              </div>}
          </>}
      </div>

      {/* Mobile back to top button */}
      <button onClick={() => window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })} className="fixed bottom-6 right-6 p-3 bg-blue-600 text-white rounded-full shadow-lg sm:hidden" data-id="element-1834">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1835">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" data-id="element-1836" />
        </svg>
      </button>
    </section>;
}